import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Card,
    Chip,
    Separator,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import type { CatalogImportRecord } from '@/api/models';
import { countParam } from '@/pages/catalogImport/notices';
import { formatInLanguage } from '@/utils/dateLocale';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ImportFigures } from './ImportFigures';
import { ImportPostingLine } from './ImportPostingLine';
import {
    IMPORT_CREATED_LABEL_KEY,
    importAmountsOf,
    importCountsOf,
    importCreatedOf,
    importPostingOf,
} from './importStatsMeta';

type ImportStatsCardProps = {
    record: CatalogImportRecord;
    /** A phone: the figures stack. */
    isNarrow: boolean;
};

// The card's title is a second-level heading under the step's title.
const asH2: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h2 {...domProps}>{children}</h2>;

/** When the import ran, in the display zone. */
const IMPORTED_AT_FORMAT = 'DD/MM/YYYY HH:mm';

/**
 * The import as the server recorded it: where and what (branch, file, rows,
 * when), the counts (products created, products imported as drafts when
 * any, variants and batches created, units), the
 * amounts (the stock's net value, the VAT taken out of its costs, its sale
 * value), the products it imported as drafts with what that means (a
 * warning alert, bos-app#221), the names it created as chips, and the
 * posting line.
 */
export const ImportStatsCard: React.FC<ImportStatsCardProps> = ({
    record,
    isNarrow,
}) => {
    const { t, i18n } = useTranslation('pages');
    const created = importCreatedOf(record);

    const meta = [
        record.fileName,
        t('catalogImport.result.stats.rows', {
            count: record.rowCount,
            rows: countParam(record.rowCount),
        }),
        record.excludedCount > 0
            ? t('catalogImport.result.stats.excluded', {
                  excluded: countParam(record.excludedCount),
              })
            : null,
        formatInLanguage(record.createdAt, IMPORTED_AT_FORMAT, i18n.language),
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <Card
            className="gap-6 p-4 sm:p-6"
            {...getNodeTestIdProp('catalog-import-result-stats')}
        >
            <Card.Header className="gap-1">
                <Card.Title
                    render={asH2}
                    {...getNodeTestIdProp('catalog-import-result-stats-title')}
                >
                    {t('catalogImport.result.stats.title', {
                        branch: record.branchName,
                    })}
                </Card.Title>
                <Card.Description
                    {...getNodeTestIdProp('catalog-import-result-stats-meta')}
                >
                    {meta}
                </Card.Description>
            </Card.Header>

            <Card.Content className="flex flex-col gap-4">
                <ImportFigures
                    figures={importCountsOf(record)}
                    isNarrow={isNarrow}
                />
                <ImportFigures
                    figures={importAmountsOf(record)}
                    isNarrow={isNarrow}
                />
                <Typography color="muted" type="body-xs">
                    {t('catalogImport.result.stats.vatNote')}
                </Typography>
                {record.productsDrafted > 0 ? (
                    <Alert
                        status="warning"
                        {...getNodeTestIdProp('catalog-import-result-drafts')}
                    >
                        <Alert.Indicator />
                        <Alert.Content>
                            <Alert.Title>
                                {t('catalogImport.result.stats.drafts', {
                                    count: record.productsDrafted,
                                    rows: countParam(record.productsDrafted),
                                })}
                            </Alert.Title>
                            <Alert.Description>
                                {t('catalogImport.result.stats.draftsHelp')}
                            </Alert.Description>
                        </Alert.Content>
                    </Alert>
                ) : null}

                <Separator />

                <div
                    className="flex flex-col gap-2"
                    {...getNodeTestIdProp('catalog-import-result-created')}
                >
                    <Typography color="muted" type="body-xs">
                        {t('catalogImport.result.stats.created.label')}
                    </Typography>
                    {created.length === 0 ? (
                        <Typography
                            type="body-sm"
                            {...getNodeTestIdProp(
                                'catalog-import-result-created-none',
                            )}
                        >
                            {t('catalogImport.result.stats.created.none')}
                        </Typography>
                    ) : (
                        <ul className="flex flex-wrap gap-2">
                            {created.map(({ kind, count }) => (
                                <li key={kind}>
                                    <Chip
                                        size="sm"
                                        variant="secondary"
                                        {...getNodeTestIdProp(
                                            `catalog-import-result-created-${kind}`,
                                        )}
                                    >
                                        {t(IMPORT_CREATED_LABEL_KEY[kind], {
                                            count,
                                            total: countParam(count),
                                        })}
                                    </Chip>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </Card.Content>

            <Card.Footer>
                <ImportPostingLine posting={importPostingOf(record)} />
            </Card.Footer>
        </Card>
    );
};
